import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials, stats } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new URLSearchParams();
      const data = new FormData(form);
      for (const [key, value] of data.entries()) {
        if (key === 'phone_alt') continue;
        formData.append(key, value.toString());
      }

      const response = await fetch('https://readdy.ai/api/form/d9sphidlbrt1qcgaj7m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore */ }

      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(msg || '提交失败，请稍后再试。');
        }
      }
    } catch (_) {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 女大学生的沙龙室',
    description: '专为在校女大学生打造的专属美发、美妆与美容体验空间。了解我们的品牌故事与服务理念。',
    url: 'https://readdy.ai/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '沙龙的服务价格对学生友好吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的！我们所有服务都提供学生专属价格，比市场价低40%-60%。新生体验套餐仅需99元，包含剪发+日常妆+基础美甲三合一服务。',
        },
      },
      {
        '@type': 'Question',
        name: '需要提前预约吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建议提前预约以确保有合适的时间段。您可以通过电话、微信或直接到店预约。周末和节假日建议提前1-2天预约。',
        },
      },
      {
        '@type': 'Question',
        name: '第一次来需要注意什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '第一次来请带上学生证（享受学生价凭证）。如果您想做发型，建议提前保存一些参考图片，方便与发型师沟通。做护肤项目前请勿化浓妆。',
        },
      },
      {
        '@type': 'Question',
        name: '可以用校园卡支付吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '支持微信支付、支付宝、现金支付。部分合作高校支持校园卡支付，具体请到店咨询。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb items={breadcrumbItems} />

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  关于我们
                </h1>
                <h2 className="text-lg md:text-xl text-primary-500 font-heading mb-4">
                  女大学生的专属美丽空间
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>女大学生的沙龙室</strong>诞生于一个简单而美好的想法：每一位女大学生都值得拥有专业、平价的美容体验。
                  </p>
                  <p>
                    我们发现，很多女大学生对美发、美妆和护肤有着强烈的需求，但市面上的沙龙要么价格高昂让人望而却步，要么质量参差不齐难以信任。于是，我们决定创建一个<strong>专属于女大学生的美丽空间</strong>——用亲民的价格，提供不输高端沙龙的专业服务。
                  </p>
                  <p>
                    我们的团队由一群热爱美业、了解年轻女性需求的专业造型师组成。每一位造型师都经过严格筛选和培训，不仅技术过硬，更重要的是他们懂得倾听，懂得如何根据每位同学的<strong>个性、脸型和需求</strong>，打造最适合的造型。
                  </p>
                  <p>
                    三年来，我们已经服务了超过5000位来自50多所高校的女大学生。她们的信任和笑容，是我们最大的动力。
                  </p>
                </div>
              </div>
              <div className="relative w-full h-[350px] md:h-[420px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20elegant%20modern%20beauty%20salon%20interior%20with%20young%20asian%20female%20staff%20working%20on%20client%20hair%2C%20team%20of%20friendly%20female%20stylists%2C%20rose%20and%20cream%20color%20palette%2C%20natural%20lighting%20from%20large%20windows%2C%20professional%20yet%20welcoming%20atmosphere%2C%20editorial%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="女大学生的沙龙室品牌故事"
                  title="女大学生的沙龙室 - 温暖专业的服务团队"
                  className="w-full h-full object-cover object-center rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-lg bg-background-50">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                她们的声音
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                每一位女大学生的认可，都是我们前进的动力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((item) => (
                <div key={item.id} className="bg-background-100 rounded-lg p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-heading font-bold text-sm">
                      {item.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.university}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-foreground-300'}`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    &ldquo;{item.content}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
            </div>

            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-lg">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.name}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                有任何问题或想预约服务？请随时联系我们
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="space-y-4 bg-background-100 rounded-lg p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  手机号
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="请输入手机号（选填）"
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  咨询主题 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="">请选择咨询主题</option>
                  <option value="appointment">预约服务</option>
                  <option value="price">价格咨询</option>
                  <option value="suggestion">建议反馈</option>
                  <option value="cooperation">合作咨询</option>
                  <option value="other">其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="请输入留言内容（最多500字）"
                />
                <p className="mt-1 text-xs text-foreground-400">最多500字</p>
              </div>
              <input type="text" name="phone_alt" className="sp-hp" tabIndex={-1} autoComplete="off" aria-hidden readOnly />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-background-50 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center bg-green-50 py-2.5 px-4 rounded-md">
                  <i className="ri-check-line mr-1" />{formMessage}
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-600 text-center bg-red-50 py-2.5 px-4 rounded-md">
                  <i className="ri-error-warning-line mr-1" />{formMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}